import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Asociado, BASE_PATH } from '../../api';
import { AuthService } from './auth.service';

export interface PaginacionAsociados {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  orden: string;
  dir: 'asc' | 'desc';
}

export interface PaginaAsociados {
  asociados: Asociado[];
  paginacion: PaginacionAsociados;
}

export interface ConsultaAsociados {
  page: number;
  pageSize: number;
  q?: string;
  /** Fragmento de DNI/NIF/NIE (la API ignora espacios y guiones). */
  nif?: string;
  asociacion?: number | null;
  orden?: string;
  dir?: 'asc' | 'desc';
}

export const TAMANOS_PAGINA = [10, 25, 50, 100];

/** La API valida el url-encoding de forma estricta (también @ + ( ) ! ' *): Angular los deja sin codificar, así que se codifica aquí. */
export function codificarParametro(valor: string): string {
  return encodeURIComponent(valor).replace(/[!'()*]/g, c => '%' + c.charCodeAt(0).toString(16).toUpperCase());
}

/**
 * Listado PAGINADO de asociados (P1.3 Wave 2.1): GET /asociados con parámetros. La paginación, la búsqueda y los filtros los
 * ejecuta MySQL; nunca se descarga la lista completa. Cada llamada es un Observable cancelable (switchMap en el componente).
 */
@Injectable({ providedIn: 'root' })
export class AsociadosListadoService {
  constructor(
    private readonly http: HttpClient,
    private readonly auth: AuthService,
    @Inject(BASE_PATH) private readonly basePath: string
  ) {}

  construirUrl(consulta: ConsultaAsociados): string {
    // paginado=true: el contrato paginado es EXPLÍCITO (sin él, GET /asociados devuelve la lista completa legacy).
    const partes: [string, string][] = [['paginado', 'true'], ['page', String(consulta.page)], ['pageSize', String(consulta.pageSize)]];
    const q = (consulta.q ?? '').trim();
    if (q) partes.push(['q', q]);
    const nif = (consulta.nif ?? '').trim();
    if (nif) partes.push(['nif', nif]);
    if (consulta.asociacion) partes.push(['asociacion', String(consulta.asociacion)]);
    if (consulta.orden) partes.push(['orden', consulta.orden]);
    if (consulta.dir) partes.push(['dir', consulta.dir]);
    return `${this.basePath}/asociados?` + partes.map(([k, v]) => `${k}=${codificarParametro(v)}`).join('&');
  }

  buscar(consulta: ConsultaAsociados): Observable<PaginaAsociados> {
    const headers = new HttpHeaders({ Authorization: 'Bearer ' + this.auth.getToken() });
    return this.http.get<PaginaAsociados>(this.construirUrl(consulta), { headers });
  }

  /** Recupera UN asociado por su ID (p. ej. la selección ya guardada de un formulario de edición): GET /asociados/{id}. */
  obtenerPorId(id: number): Observable<{ asociados?: Asociado[] }> {
    const headers = new HttpHeaders({ Authorization: 'Bearer ' + this.auth.getToken() });
    return this.http.get<{ asociados?: Asociado[] }>(`${this.basePath}/asociados/${encodeURIComponent(String(id))}`, { headers });
  }

  /** Localiza UN asociado por DNI/NIE/NIF o SIP (GET /asociados/buscar/{documento}); sirve para resolver los enlaces antiguos con NIF. */
  buscarPorDocumento(documento: string): Observable<{ asociados?: Asociado[] }> {
    const headers = new HttpHeaders({ Authorization: 'Bearer ' + this.auth.getToken() });
    return this.http.get<{ asociados?: Asociado[] }>(`${this.basePath}/asociados/buscar/${codificarParametro(documento)}`, { headers });
  }
}
